/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import type { ProviderProps } from '@/lib/types';
import 'client-only';
import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components/slots';
import type { DividerProps } from './types';

/**
 * Context for sharing Divider props across component tree.
 */
export const DividerContext =
  createContext<ContextValue<DividerProps, HTMLHRElement>>(null);

/**
 * DividerProvider - Context provider for setting default Divider props.
 *
 * Use this to configure shared orientation across multiple Divider components.
 *
 * @param props - The provider props.
 * @param props.children - Child components that will receive the divider context.
 * @returns The divider context provider wrapping children.
 *
 * @example
 * ```tsx
 * <DividerProvider orientation="vertical">
 *   <Divider />
 *   <Divider />
 * </DividerProvider>
 * ```
 */
export function DividerProvider({
  children,
  ...props
}: ProviderProps<DividerProps>) {
  return (
    <DividerContext.Provider value={props}>{children}</DividerContext.Provider>
  );
}
